import { useCallback, useState } from "react";
import type { FileWithPath } from "react-dropzone";

interface UseFileUploadOptions {
  onFilesUploaded?: (
    filesWithMetadata: Array<{
      displayName: string;
      extension: string;
      externalIdentifier: string;
      file: File;
      tags: string[];
      infosecLevelId?: string | null;
    }>,
  ) => Promise<void>;
}

export function useFileUpload(options?: UseFileUploadOptions) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState<FileWithPath[]>([]);

  const handleFilesDropped = useCallback((files: FileWithPath[]) => {
    setFilesToUpload(files);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setFilesToUpload([]);
  }, []);

  const handleFilesUploaded = useCallback(
    (
      filesWithMetadata: Array<{
        displayName: string;
        extension: string;
        externalIdentifier: string;
        file: File;
        tags: string[];
        infosecLevelId?: string | null;
      }>,
    ) => {
      const completion = options?.onFilesUploaded?.(filesWithMetadata);
      handleCloseModal();
      return completion ?? Promise.resolve();
    },
    [options, handleCloseModal],
  );

  const handleUploadButtonClick = useCallback((fileInputRef: React.RefObject<HTMLInputElement | null>) => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (files && files.length > 0) {
        handleFilesDropped(Array.from(files));
      }
      // Reset the input so the same files can be selected again
      const target = event.target;
      if (target) {
        target.value = "";
      }
    },
    [handleFilesDropped],
  );

  return {
    isModalOpen,
    filesToUpload,
    handleFilesDropped,
    handleCloseModal,
    handleFilesUploaded,
    handleUploadButtonClick,
    handleFileInputChange,
  };
}
